/**
 * Meta CAPI + Pixel dual-fire helper.
 * Sends the same event to both the browser Pixel and our FastAPI server
 * (which forwards to Meta CAPI) using a shared event_id for deduplication.
 */
import axios from "axios";
import { API } from "../App";

const uuid = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "e-" + Math.random().toString(36).slice(2) + "-" + Date.now();
};

/**
 * Manual Advanced Matching — re-init the Pixel with hashed user data so
 * Meta can match logged-in users without a CAPI token. Should be called
 * once after login/registration. Values are sent to Meta by the browser;
 * Meta's SDK hashes them before transmission when autoConfig is on.
 */
export const setAdvancedMatching = (userInfo = {}) => {
  try {
    if (typeof window === "undefined" || !window.fbq) return;
    const pid = window.__lumifit_pixel_id;
    if (!pid) return;
    const match = {};
    if (userInfo.email) match.em = String(userInfo.email).trim().toLowerCase();
    if (userInfo.phone) match.ph = String(userInfo.phone).replace(/\D+/g, "");
    if (userInfo.first_name) match.fn = String(userInfo.first_name).trim().toLowerCase();
    if (userInfo.last_name) match.ln = String(userInfo.last_name).trim().toLowerCase();
    if (userInfo.external_id) match.external_id = String(userInfo.external_id);
    if (userInfo.country) match.country = String(userInfo.country).toLowerCase();
    if (userInfo.city) match.ct = String(userInfo.city).trim().toLowerCase();
    if (Object.keys(match).length > 0) {
      window.fbq("init", pid, match);
    }
  } catch { /* silent */ }
};

/**
 * Track a Meta conversion event.
 * @param {string} eventName  Lead | StartTrial | Subscribe | Purchase | CompleteRegistration | InitiateCheckout
 * @param {object} pixelPayload  Fields for browser Pixel (value, currency, content_ids, ...)
 * @param {object} capiPayload  PII fields sent server-side only (email, phone, first_name, last_name, external_id, order_id)
 */
export const trackMetaEvent = async (eventName, pixelPayload = {}, capiPayload = {}) => {
  const eventId = uuid();

  // 1) Browser Pixel — fire first so the browser event lands even if the
  //    server call fails (Meta will still receive it and dedup later).
  try {
    if (typeof window !== "undefined" && window.fbq) {
      window.fbq("track", eventName, pixelPayload, { eventID: eventId });
    }
  } catch { /* pixel not loaded */ }

  // 2) Server-side CAPI — hashed PII, cookies, IP, UA.
  try {
    await axios.post(`${API}/meta/capi`, {
      event_name: eventName,
      event_id: eventId,
      event_source_url: window.location.href,
      ...pixelPayload,
      ...capiPayload,
    });
  } catch { /* server-side is a best-effort backup */ }

  return eventId;
};
